import { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useParams } from 'react-router-dom';
import { useStoreUnitActions } from 'src/redux/helpers';
import { useUserRoles } from 'src/auth';
import { Box } from '@mui/system';
import {
  Grid,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import PageCardLayout, { PageCardLoader } from 'src/components/PageCardLayout';
import SelectTreeView from 'src/components/SelectTreeView/SelectTreeView';
import { pepApiRequest, RequestMethod } from 'src/services';
import { isModalComplete } from 'src/components/ModalDialog';
import DataTable, {
  CellProps,
  DataTableWrapper,
  DataTableWrapperActionButton,
  getActionsColConfig,
  PagingType,
  Row,
  useTableQueryParams,
} from 'src/components/DataTable';
import { Add, Edit, Upload } from '@mui/icons-material';
import DateFormattedValue from 'src/components/DateFormattedValue/DateFormattedValue';
import { useModal } from 'src/hooks';
import TempImagePropertyMapper from 'src/components/LiveMonitor/TempImagePropertyMapper';
import { useFeatureFlagsContext } from 'src/contexts/FeatureFlagsContext';
import SolutionAssetUploadModal from './components/SolutionAssetUploadModal';
import solutionsStore, {
  useSolutionAssetAllConfigList,
  useSolutionAssetBulkDigitalTwins,
  useSolutionAssetDashboardDetails,
  useSolutionAssetMappingConfig,
  useSolutionAssetModelList,
  useSolutionDashboardList,
  useSolutionDetails,
  useSolutionSitesList,
} from '../redux';
import {
  API_GET_ASSET_DIGITALTWIN_MODEL,
  API_GET_ASSET_DIGITALTWIN_PROPERTIES,
} from '../constants';
import {
  SolutionAssetBulkDigitalTwinsItem,
  SolutionAssetConfigListItemField,
  SolutionAssetDashboardField,
  SolutionAssetHierarchyType,
  SolutionDigitalTwinType,
  SolutionMappingConfigItem,
  SolutionMappingListItemField,
} from '../types';

import SolutionAddTwinModal from './components/SolutionAddTwinModal';
import SolutionAssetEditModal from './components/SolutionAssetEditModal';
import SolutionAssetConfigAction from './components/SolutionAssetConfigAction';
import { useSolutionUserRoles } from '../hooks';
import SolutionAssetModelAction from './components/SolutionAssetModelAction';
import SolutionMappingCreateContainer from './components/SolutionMappingCreateContainer';
import SolutionMappingConfigAction from './components/SolutionMappingConfigAction';
import SolutionAssetDashboardCreateContainer from './components/SolutionAssetDashboardCreateContainer';
import SolutionAssetDashboardActions from './components/SolutionAssetDashboardActions';
import SolutionDashboardCreateModal from './components/SolutionDashboardCreateModal';
import SolutionDashboardActions from './components/SolutionDashboardActions';
import SolutionDashboardViewModal from './components/SolutionDashboardViewModal';
import SolutionOmniverseApplication from './components/SolutionOmniverseApplication';
import SolutionAssetModelUploadModal from './components/SolutionAssetModelUploadModal';
import DataSimulation from './simulation/DataSimulation';

export interface DisplayData {
  key?: string;
  value: string;
  schema?: string | Record<string, any>;
  name?: string;
  visible?: boolean;
}

const cellStyle = {
  border: 0,
  height: 'auto',
  padding: '10px 20px 10px 0px',
};
const LabelStyle = {
  fontWeight: 'bold',
  width: '23%',
  textTransform: 'capitalize',
};

const columns = [
  {
    id: SolutionAssetConfigListItemField.fileName,
    title: 'Name',
  },
  {
    id: SolutionAssetConfigListItemField.updatedOn,
    title: 'Timestamp',
    Component: DateFormattedValue,
  },
  {
    id: SolutionAssetConfigListItemField.userId,
    title: 'Created by',
  },
  getActionsColConfig(SolutionAssetConfigAction),
];

const AssetDataColumns = (solutionId: string, twinId: string) => [
  {
    id: SolutionAssetConfigListItemField.fileName,
    title: 'Name',
  },
  {
    id: SolutionAssetConfigListItemField.size,
    title: 'Size',
  },
  {
    id: SolutionAssetConfigListItemField.mimeType,
    title: 'File Type',
  },
  {
    id: SolutionAssetConfigListItemField.updatedOn,
    title: 'Timestamp',
    Component: DateFormattedValue,
  },
  {
    id: SolutionAssetConfigListItemField.userEmail,
    title: 'Created by',
  },
  getActionsColConfig((props: any) => (
    <SolutionAssetModelAction
      {...props}
      solutionId={solutionId}
      twinId={twinId}
    />
  )),
];

const AssetMapDataColumns = [
  {
    id: SolutionMappingListItemField.sourceType,
    title: 'Source',
  },
  {
    id: SolutionMappingListItemField.destinationType,
    title: 'Destination',
  },
  {
    id: SolutionMappingListItemField.path,
    title: 'USD File',
    Component: (props: any) => {
      const { value } = props;
      return (
        <Typography fontSize="16px">
          {typeof value === 'string'
            ? value.split('ov/')[1]?.replace(/\/$/, '') || ''
            : '-'}
        </Typography>
      );
    },
  },
  getActionsColConfig(SolutionMappingConfigAction),
];

const AssetDasboardDataColumns = [
  {
    id: SolutionAssetDashboardField.Name,
    title: 'Name',
  },
  getActionsColConfig(SolutionAssetDashboardActions),
];

const DashboardDataColumns = [
  {
    id: SolutionAssetDashboardField.Name,
    title: 'Name',
  },
  getActionsColConfig(SolutionDashboardActions),
];

export default function SolutionAssetHierarchy() {
  const [solutionDetails] = useSolutionDetails();
  const [assetData, assetLoading] = useSolutionSitesList();
  const [assetBulkDigitalTwinsData, digitalTwinDataLoading] =
    useSolutionAssetBulkDigitalTwins();
  const defaultItem = [{ id: 'PepsiCo', level: 0 }];
  const { solutionId } = useParams();
  const { isPlatformReader, isPlatformEngineer } = useUserRoles();
  const { isValidSolutionUser, isSolutionOwner } =
    useSolutionUserRoles(solutionId);
  const actions = useStoreUnitActions(solutionsStore);
  const { isOmniverseEnabled } = useFeatureFlagsContext();
  const [siteIds, setSiteIds] = useState<any[]>([]);
  const [loadingPropertyDetails, setLoadingPropertyDetails] =
    useState<boolean>(false);
  const [displayData2, setDisplayData2] = useState<DisplayData[]>([]);
  const prevSiteIdsRef = useRef<string[]>([]);
  const { data, loading, totalItems } = useSolutionAssetAllConfigList();
  const { data: assetModelData, loading: assetModelLoading } =
    useSolutionAssetModelList();
  const [assetMappingData, loadingMappingData] =
    useSolutionAssetMappingConfig();
  const [assetDashboardData, assetDashboardLoading] =
    useSolutionAssetDashboardDetails();
  const [dashboardList, dashboardListLoading] = useSolutionDashboardList();
  const [sitesId, setSiteId] = useState('');
  const [id, setId] = useState('');
  const [level, setLevel] = useState(0);
  const isAssetEditor =
    isPlatformEngineer || isPlatformReader || isValidSolutionUser;
  const { isValidSolutionOwner } = useSolutionUserRoles(solutionId);
  const [loader, setLoader] = useState(false);
  const [propertiesData, setPropertiesData] = useState([]);
  const [rawDtdlModel, setRawDtdlModel] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [rawTwinInstance, setRawTwinInstance] = useState<Record<
    string,
    unknown
  > | null>(null);
  const { paging, sorting } = useTableQueryParams();
  const [node, setNode] = useState<any>({});
  const [dashboardViewState, setDashboardViewState] = useState<{
    isViewingDashboard: boolean;
    selectedDashboard?: any;
  }>({
    isViewingDashboard: false,
    selectedDashboard: null,
  });
  const { pageIndex, pageSize } = paging;
  const { sortBy, sortOrder } = sorting;
  const isISA95Standard = solutionDetails?.isIsa95Standard || false;

  const DashboardDataColumnsWithActions = useMemo(
    () => [
      {
        id: SolutionAssetDashboardField.Name,
        title: 'Name',
      },
      getActionsColConfig((props) => (
        <SolutionDashboardActions
          {...props}
          onView={(item) =>
            setDashboardViewState({
              isViewingDashboard: true,
              selectedDashboard: item,
            })
          }
        />
      )),
    ],
    []
  );

  const [type, setType] = useState(SolutionAssetHierarchyType.Properties);
  const [activeTab, setActiveTab] = useState<SolutionDigitalTwinType>(
    isOmniverseEnabled
      ? SolutionDigitalTwinType.Omniverse
      : SolutionDigitalTwinType.LiveMonitor
  );

  const handleChange = (event: any, newValue) => {
    setType(newValue);
  };

  const handleTabChange = (event: any, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    actions.sites(solutionId);
    actions.details(solutionId);
  }, []);

  useEffect(() => {
    if (level !== 0) {
      if (type === SolutionAssetHierarchyType.ADX_Dashboard) {
        actions.assetDashboard({ solutionId, twinId: id });
      }
      if (type === SolutionAssetHierarchyType.MappingConfiguration) {
        actions.assetMappingConfig({
          solutionId,
          siteId: !isISA95Standard ? '00' : sitesId,
          twinId: id,
        });
      }
      if (type === SolutionAssetHierarchyType.AssetModel) {
        actions.assetModelData({ solutionId, twinId: id });
      }
      if (type === SolutionAssetHierarchyType.Dashboard) {
        actions.dashboardList({ solutionId, twinId: id });
      }
    }
  }, [type, id, sitesId]);

  useEffect(() => {
    if (level !== 0 && activeTab === SolutionDigitalTwinType.Omniverse && id) {
      actions.omniverseApp({
        solutionId,
        siteId: sitesId,
        twinId: id,
        functionId: '',
        functionVersionId: '',
      });
    }
  }, [activeTab, id, level, sitesId, solutionId]);

  useEffect(() => {
    if (level !== 0 && type === SolutionAssetHierarchyType.Configurations) {
      actions.assetConfigList({
        solutionId,
        twinId: id,
        pageIndex,
        pageSize,
        sortOrder: 'desc',
      });
    }
  }, [level, type, solutionId, id, pageIndex, pageSize, sortOrder]);

  useEffect(() => {
    if (
      assetLoading ||
      digitalTwinDataLoading ||
      loading ||
      loadingPropertyDetails ||
      loadingMappingData ||
      assetDashboardLoading ||
      assetModelLoading ||
      dashboardListLoading
    ) {
      setLoader(true);
    } else {
      setLoader(false);
    }
  }, [
    assetLoading,
    digitalTwinDataLoading,
    loading,
    loadingPropertyDetails,
    loadingMappingData,
    assetDashboardLoading,
    assetModelLoading,
    dashboardListLoading,
  ]);

  const handleSelect = (node: SolutionAssetBulkDigitalTwinsItem) => {
    setLoadingPropertyDetails(true);
    setNode(node);
    setDashboardViewState({
      isViewingDashboard: false,
      selectedDashboard: null,
    });
  };

  useEffect(() => {
    let cancelled = false;
    const { siteId, level, id } = node;
    setId(id);
    setLevel(level);
    const computedSiteId = !isISA95Standard ? '00' : siteId;
    setSiteId(computedSiteId);

    // Clear previous twin data immediately to avoid showing stale properties
    setDisplayData2([]);
    setPropertiesData([]);
    setRawDtdlModel(null);
    setRawTwinInstance(null);

    if (
      siteId &&
      (!isISA95Standard || (isISA95Standard && level >= 1)) &&
      (type === SolutionAssetHierarchyType.Properties ||
        activeTab === SolutionDigitalTwinType.LiveMonitor ||
        activeTab === SolutionDigitalTwinType.DataSimulation)
    ) {
      setSiteId(computedSiteId);
      setId(id);
      setLevel(level);
      setLoader(true);
      pepApiRequest({
        method: RequestMethod.Get,
        url: generatePath(API_GET_ASSET_DIGITALTWIN_PROPERTIES, {
          solutionId,
          siteId: computedSiteId,
          twinId: id,
        }),
      }).then((response) => {
        if (cancelled) return;
        const newAssetProperties = response;

        const modelId = newAssetProperties?.$metadata?.$model;
        if (modelId) {
          pepApiRequest({
            method: RequestMethod.Get,
            url: generatePath(API_GET_ASSET_DIGITALTWIN_MODEL, {
              solutionId,
              modelId,
            }),
          }).then((result) => {
            if (cancelled) return;
            const newDigitalTwinModel = result[0];

            const filteredAssetProperties: any = Object.entries(
              newAssetProperties
            ).filter(
              ([key]) =>
                key !== '$etag' &&
                key !== '$metadata' &&
                key !== 'solutionId' &&
                key !== 'siteID' &&
                key !== '$dtId' &&
                key !== 'uId'
            );
            setPropertiesData(filteredAssetProperties);
            const displayData: DisplayData[] =
              newDigitalTwinModel?.contents?.map((content) => {
                const assetValue = filteredAssetProperties.find(
                  ([key]) => key.replace('$', '') === content.name
                );
                return {
                  key: content.displayName || content.name,
                  value: assetValue ? assetValue[1] : '',
                  schema: content.schema,
                  name: content.name || '',
                };
              }) || [];

            ['source', 'type'].forEach((key) => {
              if (newAssetProperties[key] !== undefined) {
                displayData.push({
                  key,
                  value: newAssetProperties[key],
                  schema: '',
                  name: '',
                });
              }
            }, []);
            setDisplayData2(displayData);
            setRawDtdlModel(newDigitalTwinModel ?? null);
            setRawTwinInstance(newAssetProperties ?? null);
            setLoader(false);
            setLoadingPropertyDetails(false);
          });
        } else {
          if (cancelled) return;
          setDisplayData2([]);
          setRawDtdlModel(null);
          setRawTwinInstance(null);
          setLoadingPropertyDetails(false);
        }
      });
    } else {
      setLoadingPropertyDetails(false);
    }

    return () => {
      cancelled = true;
    };
  }, [node, type, activeTab, id, isISA95Standard, solutionId]);

  const effectiveSiteIds = useMemo(() => {
    if (!assetData) return [];
    const ids = assetData.map((item) => String(item.id));
    return !isISA95Standard ? ['00'] : ids;
  }, [assetData, isISA95Standard]);

  useEffect(() => {
    setSiteIds(effectiveSiteIds);
  }, [effectiveSiteIds]);

  useEffect(() => {
    if (effectiveSiteIds.length > 0) {
      const prevSiteIds = prevSiteIdsRef.current;
      const isDifferent =
        JSON.stringify(prevSiteIds) !== JSON.stringify(effectiveSiteIds);

      if (isDifferent) {
        actions.assetBulkDigitalTwins({
          solutionId,
          siteIds: effectiveSiteIds,
          twinId: 'PepsiCo',
        });
        prevSiteIdsRef.current = effectiveSiteIds;
      }
    }
  }, [effectiveSiteIds, solutionId]);

  const validData = displayData2?.filter((item) => item.schema !== '');

  const [UploadNewAssetDialog, showNewUploadAssetDialog] = useModal(
    SolutionAssetUploadModal,
    {
      sitesId,
      id,
      pageIndex,
      pageSize,
      sortOrder,
      onClose: (event, reason) => isModalComplete(reason),
    }
  );

  const [UploadNew3DAssetModelDialog, showNewUpload3DAssetModelDialog] =
    useModal(SolutionAssetModelUploadModal, {
      id,
      onClose: (event, reason) => isModalComplete(reason),
    });

  const [AddNewTwinDialog, showNewTwinDialog] = useModal(SolutionAddTwinModal, {
    id,
    siteIds,
    level,
    setLoader,
    sitesId,
    onClose: (event, reason) => isModalComplete(reason),
  });

  const [EditAssetDialog, showEditAssetDialog] = useModal(
    SolutionAssetEditModal,
    {
      sitesId,
      id,
      displayData2,
      propertiesData,
      level,
      setNode,
      setLoader,
      onClose: (event, reason) => isModalComplete(reason),
    }
  );

  const [CreateNewMappingConfigDialog, showNewMappingConfigDialog] = useModal(
    SolutionMappingCreateContainer,
    {
      sitesId,
      id,
      onClose: (event, reason) => isModalComplete(reason),
    }
  );

  const [NewDashboardCreationDialog, showNewDashboardDialog] = useModal(
    SolutionAssetDashboardCreateContainer,
    {
      id,
      onClose: (event, reason) => isModalComplete(reason),
    }
  );

  const [dashboardCreationDialog, showDashboardDialog] = useModal(
    SolutionDashboardCreateModal,
    {
      id,
      onClose: (event, reason) => isModalComplete(reason),
    }
  );

  const getValue = (item) => {
    const { schema } = item;
    const schemaType =
      typeof schema === 'object' && schema !== null ? schema['@type'] : schema;

    if (schemaType === 'boolean') {
      if (item?.value === true) {
        return 'True';
      }
      if (item?.value === false) {
        return 'False';
      }
      return '-';
    }
    if (schemaType === 'Enum') {
      return (
        schema?.enumValues?.find((a) => a.enumValue === item.value)?.name || '-'
      );
    }
    if (schemaType === 'dateTime') {
      return <DateFormattedValue value={item.value} defaultValue="-" />;
    }
    if (item.value === undefined || item.value === null || item.value === '') {
      return <Typography fontSize="24px">-</Typography>;
    }
    if (typeof item.value === 'object' && item.value !== null) {
      return JSON.stringify(item.value);
    }
    return item.value;
  };

  return (
    <Box sx={{ flexGrow: 1, padding: 0, minHeight: '60vh' }}>
      <Grid container>
        <Grid item xs={12} md={3}>
          <PageCardLayout
            id="asset-hierarchy-tree-view"
            sx={{ height: '100%' }}
          >
            {id && (
              <Box
                sx={{
                  minHeight: '10px',
                  borderBottom: '1px #E8E8E8 solid',
                  textAlignLast: 'right',
                }}
              >
                <DataTableWrapperActionButton
                  sx={{ marginBottom: '10px' }}
                  onClick={showNewTwinDialog}
                  disabled={
                    isPlatformEngineer ||
                    isPlatformReader ||
                    (isISA95Standard && level === 0)
                  }
                >
                  <Add /> Add Twin
                </DataTableWrapperActionButton>
              </Box>
            )}
            <SelectTreeView
              items={
                assetBulkDigitalTwinsData &&
                assetBulkDigitalTwinsData.length > 0
                  ? assetBulkDigitalTwinsData
                  : defaultItem
              }
              onItemClick={handleSelect}
            />
          </PageCardLayout>
        </Grid>
        <Grid item xs={12} md={9}>
          <PageCardLayout
            id="asset-hierarchy-details"
            sx={{ height: '100%', minHeight: '60vh', overflowY: 'scroll' }}
            title={id ? `${id} Details` : 'Details'}
          >
            <Tabs
              value={type}
              onChange={handleChange}
              aria-label="wrapped label tabs example"
              sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 3 }}
            >
              <Tab
                value={SolutionAssetHierarchyType.Properties}
                label="Properties"
              />
              <Tab
                value={SolutionAssetHierarchyType.Digital_Twin}
                label="Digital Twin"
              />
              <Tab
                value={SolutionAssetHierarchyType.Configurations}
                label="Asset Configurations"
              />
              <Tab
                value={SolutionAssetHierarchyType.AssetModel}
                label="3D Asset Models"
              />
              <Tab
                value={SolutionAssetHierarchyType.MappingConfiguration}
                label="Digital Twin Configurations"
              />
              <Tab
                value={SolutionAssetHierarchyType.ADX_Dashboard}
                label="ADX Dashboard"
              />
              <Tab
                value={SolutionAssetHierarchyType.Dashboard}
                label="Dashboard"
              />
            </Tabs>
            {type === SolutionAssetHierarchyType.Properties && (
              <>
                <DataTableWrapper
                  searchBar={false}
                  actionsPanel={
                    <DataTableWrapperActionButton
                      startIcon={<Edit />}
                      onClick={showEditAssetDialog}
                      disabled={
                        !isValidSolutionOwner ||
                        level === 0 ||
                        validData.length === 0
                      }
                    >
                      Edit
                    </DataTableWrapperActionButton>
                  }
                ></DataTableWrapper>
                <Table>
                  <TableBody>
                    {displayData2.length > 0 ? (
                      displayData2.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell sx={[cellStyle, LabelStyle]}>
                            {item.key}
                          </TableCell>
                          <TableCell sx={cellStyle}>{getValue(item)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="#A5A5A5"
                            align="center"
                          >
                            No Properties to display
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </>
            )}
            {type === SolutionAssetHierarchyType.Configurations && (
              <DataTableWrapper
                actionsPanel={
                  <DataTableWrapperActionButton
                    startIcon={<Upload />}
                    onClick={showNewUploadAssetDialog}
                    disabled={
                      !isValidSolutionOwner || level === 0 || id === undefined
                    }
                  >
                    Upload
                  </DataTableWrapperActionButton>
                }
              >
                <DataTable
                  data={level === 0 ? [] : data}
                  columns={columns}
                  paging={PagingType.Remote}
                  totalItems={totalItems}
                  {...paging}
                  {...sorting}
                />
              </DataTableWrapper>
            )}
            {type === SolutionAssetHierarchyType.AssetModel && (
              <DataTableWrapper
                searchBar={false}
                actionsPanel={
                  <DataTableWrapperActionButton
                    startIcon={<Upload />}
                    onClick={showNewUpload3DAssetModelDialog}
                    disabled={
                      !isValidSolutionOwner || level === 0 || id === undefined
                    }
                  >
                    Upload
                  </DataTableWrapperActionButton>
                }
              >
                <DataTable
                  columns={AssetDataColumns(solutionId || '', id)}
                  data={assetModelData}
                  paging={PagingType.Remote}
                />
              </DataTableWrapper>
            )}
            {type === SolutionAssetHierarchyType.MappingConfiguration && (
              <DataTableWrapper
                searchBar={true}
                actionsPanel={
                  <DataTableWrapperActionButton
                    startIcon={<Add />}
                    onClick={showNewMappingConfigDialog}
                    disabled={
                      !isSolutionOwner || level === 0 || id === undefined
                    }
                  >
                    Create New
                  </DataTableWrapperActionButton>
                }
              >
                <DataTable
                  data={assetMappingData ?? []}
                  loading={false}
                  columns={AssetMapDataColumns}
                />
                {CreateNewMappingConfigDialog}
              </DataTableWrapper>
            )}
            {type === SolutionAssetHierarchyType.ADX_Dashboard && (
              <DataTableWrapper
                searchBar={true}
                actionsPanel={
                  <DataTableWrapperActionButton
                    startIcon={<Add />}
                    onClick={showNewDashboardDialog}
                    disabled={
                      !isSolutionOwner || level === 0 || id === undefined
                    }
                  >
                    Add New
                  </DataTableWrapperActionButton>
                }
              >
                <DataTable
                  data={assetDashboardData ?? []}
                  loading={false}
                  columns={AssetDasboardDataColumns}
                />
                {NewDashboardCreationDialog}
              </DataTableWrapper>
            )}
            {type === SolutionAssetHierarchyType.Dashboard &&
              (dashboardViewState.isViewingDashboard ? (
                <SolutionDashboardViewModal
                  item={dashboardViewState.selectedDashboard}
                  onClose={() =>
                    setDashboardViewState({
                      isViewingDashboard: false,
                      selectedDashboard: null,
                    })
                  }
                />
              ) : (
                <DataTableWrapper
                  searchBar={true}
                  actionsPanel={
                    <DataTableWrapperActionButton
                      startIcon={<Add />}
                      onClick={showDashboardDialog}
                      disabled={
                        isPlatformReader || level === 0 || id === undefined
                      }
                    >
                      Add New
                    </DataTableWrapperActionButton>
                  }
                >
                  <DataTable
                    data={(dashboardList ?? []) as any}
                    loading={false}
                    columns={DashboardDataColumnsWithActions}
                  />
                  {dashboardCreationDialog}
                </DataTableWrapper>
              ))}
            {type === SolutionAssetHierarchyType.Digital_Twin && (
              <>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  aria-label="digital twin tabs"
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    marginBottom: 3,
                  }}
                >
                  {isOmniverseEnabled && (
                    <Tab
                      value={SolutionDigitalTwinType.Omniverse}
                      label="Omniverse"
                    />
                  )}
                  <Tab
                    value={SolutionDigitalTwinType.LiveMonitor}
                    label="Live Monitor"
                  />
                  <Tab
                    value={SolutionDigitalTwinType.DataSimulation}
                    label="Synthetic Data Generation"
                  />
                </Tabs>

                {isOmniverseEnabled &&
                  activeTab === SolutionDigitalTwinType.Omniverse && (
                    <SolutionOmniverseApplication
                      siteId={sitesId}
                      twinId={id}
                      levelId={level}
                    />
                  )}

                {activeTab === SolutionDigitalTwinType.LiveMonitor &&
                displayData2.length > 0 ? (
                  <div style={{ width: '100%', height: '100vh' }}>
                    <TempImagePropertyMapper
                      key={`${id}-${sitesId}`}
                      displayData={[
                        ...displayData2.map((data) => ({
                          ...data,
                          visible: true,
                        })),
                      ]}
                      twinId={id}
                      solutionId={solutionId || ''}
                      siteId={!isISA95Standard ? '00' : sitesId}
                    />
                  </div>
                ) : (
                  activeTab === SolutionDigitalTwinType.LiveMonitor && (
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="#A5A5A5"
                              align="center"
                            >
                              No Data to display
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  )
                )}

                {activeTab === SolutionDigitalTwinType.DataSimulation && (
                  <DataSimulation
                    dtdlModel={rawDtdlModel}
                    twinInstance={rawTwinInstance}
                    twinId={id}
                    solutionId={solutionId || ''}
                    siteId={!isISA95Standard ? '00' : sitesId}
                  />
                )}
              </>
            )}
          </PageCardLayout>
        </Grid>
      </Grid>
      {loader && <PageCardLoader />}
      {UploadNewAssetDialog}
      {UploadNew3DAssetModelDialog}
      {AddNewTwinDialog}
      {EditAssetDialog}
      {NewDashboardCreationDialog}
    </Box>
  );
}
